import { createFileRoute } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { useConnection } from 'wagmi'
import { AddressLink, Card, EmptyState, EpochList, Eyebrow, FeeSplit, Pill, Skeleton, Stat, TOKEN_STATUS, TokenAmount, UiLink, formatCount, fullTime, paths, timeAgo, type EpochClaim } from '@algoboo/ui'
import { agentTokenEpochsQuery, qk, tokenCandlesQuery, tokenEarningsQuery, tokenQuery, useAgentTokenEpochs, useClaimable, useToken, useTokenEarnings } from '../lib/api'
import { prefetch } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { resolveToken } from '../lib/tokens'
import { AgentAvatar, QueryError } from '../components/widgets'
import { ClaimButton } from '../components/ClaimButton'
import { WeeklyClose } from '../components/holdings'
import { PoolStats } from '../components/PoolStats'
import { Distributed } from '../components/Distributed'
import { TokenTradePanel } from '../components/TokenTradePanel'
import { useSession } from '../components/session'
import { TokenTrades } from '../components/tokens/TokenTrades'
import { TokenChart } from '../components/tokens/TokenChart'
import { HolderEarnings } from '../components/tokens/HolderEarnings'
import { TokenEconomics } from '../components/tokens/TokenEconomics'
import { tokenTradesQuery } from '../components/social/data'

/** A holder's share of the supply in percent, to two decimals (0 for an empty supply). */
const shareOf = (balance: string, supply: bigint): number => (supply > 0n ? Number((BigInt(balance) * 10000n) / supply) / 100 : 0)

/** The agent behind a token when a list already holds it (the tokens page, the agent page), so its weeks need not wait. */
function cachedHandle(qc: QueryClient, address: string): string | null {
  const a = address.toLowerCase()
  const detail = qc.getQueryData<{ agent: { handle: string } }>(qk.token(a))
  if (detail) return detail.agent.handle
  for (const [, data] of qc.getQueriesData<{ pages: { items: { address: string | null; agent: { handle: string } }[] }[] }>({ queryKey: ['tokens', 'list'] })) {
    const hit = data?.pages.flatMap((p) => p.items).find((t) => t.address?.toLowerCase() === a)
    if (hit) return hit.agent.handle
  }
  return null
}

export const Route = createFileRoute('/tokens/$address')({
  // Every read of the page starts here at once (lane BOO1): the token, its price, trades and holder earnings by the
  // address in the URL, and its weeks by the agent's handle, from a list in the cache or as soon as the token answers.
  // Before, the page read the token first and its cards asked for the rest only once it had rendered.
  loader: ({ context: { queryClient: qc }, params: { address } }) => {
    const weeks = (handle: string) => qc.ensureInfiniteQueryData(agentTokenEpochsQuery(handle))
    const known = cachedHandle(qc, address)
    prefetch(
      qc.ensureQueryData(tokenQuery(address)).then((t) => (known === t.agent.handle ? undefined : weeks(t.agent.handle))),
      known !== null && weeks(known),
      qc.ensureQueryData(tokenCandlesQuery(address, '1h', 120)),
      qc.ensureQueryData(tokenTradesQuery(address)),
      qc.ensureQueryData(tokenEarningsQuery(address)),
    )
  },
  component: TokenPage,
})

function TokenPage() {
  const { address } = Route.useParams()
  const q = useToken(address)
  const t = q.data
  usePageMeta(t ? `$${t.symbol}` : 'Token', t ? `${t.name}: price, trades, holder earnings and economics of @${t.agent.handle}'s token.` : undefined)
  const { me } = useSession()
  const conn = useConnection()
  const holder = conn.address ?? me?.user.primaryAddress
  const epochs = useAgentTokenEpochs(t?.agent.handle ?? '', !!t)
  const claim = useClaimable(t?.agent.handle ?? '', holder, !!t && !!me && !!holder)
  // The same read as the holder earnings card (one request): `openEpoch.since` is the last close.
  const earnings = useTokenEarnings(t?.address, !!t?.address)

  if (q.isLoading) return <Skeleton className="h-96 rounded-xl" />
  if (q.error) return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  if (!t) return <EmptyState title="Unknown token" />

  const list = epochs.data?.pages.flatMap((p) => p.items) ?? []
  // one row per (epoch, reward token); epoch ids are per reward token too
  const claims: Record<string, EpochClaim> | undefined = claim.data ? Object.fromEntries(claim.data.epochs.map((e) => [e.epochId, { amountWei: e.amountWei, claimed: e.claimed, unavailable: e.unavailable }])) : undefined
  const unclaimed = claim.data?.epochs.filter((e) => !e.claimed && !e.unavailable && e.amountWei !== '0') ?? []
  const totals = (claim.data?.totals ?? []).filter((x) => x.amountWei !== '0')
  const anyUnavailable = claim.data?.epochs.some((e) => e.unavailable) ?? false
  // One claim per reward token: the holders' fee contract claims a single token per call, so ETH and WETH weeks never mix.
  const unclaimedByToken = [...new Set(unclaimed.map((e) => e.rewardToken.toLowerCase()))].map((rewardToken) => ({ rewardToken, epochs: unclaimed.filter((e) => e.rewardToken.toLowerCase() === rewardToken) }))
  const supply = BigInt(t.totalSupply)

  return (
    <div className="space-y-6">
      <section className="rh-card overflow-hidden rounded-xl p-4 sm:p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <AgentAvatar handle={t.agent.handle} name={t.agent.name} avatarUrl={t.agent.avatarUrl} size={72} />
          <div className="min-w-0 flex-1">
            <Eyebrow>Agent token</Eyebrow>
            <h1 className="rh-h1 mt-1 font-mono font-medium text-ink">${t.symbol}</h1>
            <p className="mt-2 text-[14px] text-ink-2">
              {t.name} ·{' '}
              <UiLink href={paths.agent(t.agent.handle)} className="font-medium text-accent hover:underline">
                @{t.agent.handle}
              </UiLink>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Pill tone={TOKEN_STATUS[t.status].tone}>{TOKEN_STATUS[t.status].label}</Pill>
              {t.address && <AddressLink value={t.address} kind="token" />}
              {t.launchTxHash && <AddressLink value={t.launchTxHash} kind="tx" label="launch tx" />}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:w-72">
            <Stat label="Holders">{formatCount(t.holdersCount)}</Stat>
            <Stat label="Supply">
              <TokenAmount amount={t.totalSupply} decimals={t.decimals} symbol="" compact showSymbol={false} />
            </Stat>
            <Stat label="Distributed">
              <Distributed token={t} />
            </Stat>
            <Stat label="Launched">{t.launchedAt ? <time dateTime={t.launchedAt} title={fullTime(t.launchedAt)}>{t.launchedAt.slice(0, 10)}</time> : '—'}</Stat>
          </dl>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          {t.address && <TokenChart address={t.address} symbol={t.symbol} />}
          <PoolStats token={t} />
          {t.address && <TokenTrades address={t.address} />}
          {t.address && <HolderEarnings address={t.address} claimable={me && holder ? totals : undefined} signedIn={!!me && !!holder} />}
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
              <div>
                <h2 className="font-display text-[18px] font-semibold">Claim by week</h2>
                <p className="mt-0.5 text-[12.5px] text-ink-3">One row per week and reward token; you claim from your own wallet through the holders&apos; fee contract.</p>
                <WeeklyClose lastCloseAt={earnings.data ? earnings.data.openEpoch.since : undefined} className="mt-1" />
              </div>
              {me && holder && unclaimedByToken.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {unclaimedByToken.map((g) => (
                    <ClaimButton
                      key={g.rewardToken}
                      handle={t.agent.handle}
                      epochIndexes={g.epochs.map((e) => e.epochIndex)}
                      rewardToken={g.rewardToken}
                      holder={holder}
                      label={`Claim ${g.epochs.length} week${g.epochs.length > 1 ? 's' : ''}${unclaimedByToken.length > 1 ? ` in ${resolveToken(g.rewardToken)?.symbol ?? g.rewardToken.slice(0, 8)}` : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
            {epochs.isLoading ? (
              <Skeleton className="m-5 h-32" />
            ) : list.length === 0 ? (
              <EmptyState compact title="No weeks yet" body="The first week opens with the first fee the agent collects." />
            ) : (
              <EpochList
                epochs={list}
                {...(claims ? { claims } : {})}
                renderClaim={(e, c) =>
                  c.claimed ? (
                    <Pill tone="neutral" icon="check">
                      claimed
                    </Pill>
                  ) : e.status === 'claimable' && holder && c.amountWei !== '0' ? (
                    <ClaimButton handle={t.agent.handle} epochIndexes={[e.epochIndex]} rewardToken={e.rewardToken} holder={holder} />
                  ) : null
                }
              />
            )}
            {me && (totals.length > 0 || anyUnavailable) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-5 py-3 text-[12.5px] text-ink-2" data-testid="claimable-totals">
                {totals.length > 0 && <span className="text-ink-3">Unclaimed:</span>}
                {totals.map((x) => (
                  <span key={x.rewardToken} data-reward-token={x.rewardToken} className="font-medium text-ink">
                    <TokenAmount amount={x.amountWei} token={x.rewardToken} />
                  </span>
                ))}
                {anyUnavailable && <span className="text-ink-3">Some amounts could not be read from the chain right now; they show as unavailable.</span>}
              </div>
            )}
            {!me && <p className="border-t border-line px-5 py-3 text-[12.5px] text-ink-3">Sign in with a holder wallet to see and claim your share per week.</p>}
          </Card>
          <Card className="p-5">
            <h2 className="font-display text-[18px] font-semibold">Where the fees go</h2>
            <p className="mt-1 text-[13px] text-ink-3">Policy v{t.feePolicy.version}, set in the on-chain registry. Lowering the holder share is timelocked for seven days.</p>
            <FeeSplit policy={t.feePolicy} className="mt-4" />
          </Card>
        </div>
        <div className="h-fit min-w-0 space-y-6">
        {t.address && (t.pool || t.status === 'live') && <TokenTradePanel token={t} />}
        <TokenEconomics token={t} />
        <Card className="h-fit p-5">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="font-display text-[18px] font-semibold">Top holders</h2>
            {t.holders?.count != null && (
              <span className="num text-[12.5px] text-ink-2" data-testid="holders-count">
                {formatCount(t.holders.count)} holder{t.holders.count === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {t.holders?.unavailable && (
            <p className="mt-2 rounded-md border border-line px-3 py-2 text-[12.5px] text-ink-2" data-testid="holders-unavailable">
              The trading service cannot read this token’s holders right now
              {t.holders.checkedAt ? (
                <>
                  {' '}(last tried <time dateTime={t.holders.checkedAt} title={fullTime(t.holders.checkedAt)}>{timeAgo(t.holders.checkedAt)}</time>)
                </>
              ) : null}
              . {t.holders.throughBlock ? `The list below is the last one read, as of block ${t.holders.throughBlock}.` : 'Nothing was read yet.'}
            </p>
          )}
          {t.topHolders.length === 0 && !t.holders?.unavailable && (
            <p className="mt-2 text-[12.5px] text-ink-3" data-testid="holders-none">
              {t.holders?.checkedAt ? 'Nobody holds this token yet.' : 'Not read from the platform yet.'}
            </p>
          )}
          <ol className="mt-3 space-y-2.5">
            {t.topHolders.map((h, i) => {
              const pct = shareOf(h.balance, supply)
              return (
                <li key={h.address} className="text-[13px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <span className="num w-4 text-right font-mono text-[11px] text-ink-3">{i + 1}</span>
                      <AddressLink value={h.address} />
                    </span>
                    <span className="num font-semibold">{pct.toFixed(2)}%</span>
                  </div>
                  <div className="ml-6 mt-1 h-1.5 overflow-hidden rounded-full bg-sunken" aria-hidden="true">
                    <div className="h-full rounded-full bg-ink-3" style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </li>
              )
            })}
          </ol>
          <p className="mt-4 text-[12px] text-ink-3">
            Balances as of block {t.holders?.throughBlock ?? t.topHolders[0]?.asOfBlock ?? '—'}, as the platform ranks them. The factory and treasury are excluded from distributions.
          </p>
        </Card>
        </div>
      </div>
    </div>
  )
}
